<template>
  <div class="flex flex-col items-center">
    <PackageCoverCard
      ref="packageCoverRef"
      :package="pkg"
      :width="actualWidth"
      class="w-full"
      @share-click="onShareEdit"
      @menu-click="toggleMenu"
    />

    <DebugLabel>
      <div class="text-xs">id：{{ pkg.id }}</div>
      <template v-if="isOwner">
        <div class="text-xs">创建时间：{{ $f.date(pkg.createdAt) }}</div>
        <div class="text-xs">最近编辑：{{ $f.date(pkg.updatedAt) }}</div>
        <div class="text-xs">
          最近快照：{{
            pkg.lastSnapshotAt ? $f.date(pkg.lastSnapshotAt) : '无'
          }}
        </div>
      </template>

      <template v-else-if="pkg.shelf">
        <div class="text-xs">
          添加时间：{{ $f.date(pkg.shelf.addedShelfAt) }}
        </div>
        <div
          v-if="pkg.lastSnapshotAt"
          class="text-xs"
        >
          最近快照：{{ $f.date(pkg.lastSnapshotAt) }}
        </div>
      </template>
    </DebugLabel>

    <OverlayPanel ref="menuOp">
      <div class="flex flex-col gap-4">
        <template v-if="isOwner">
          <div
            class="cursor-pointer"
            @click="onEdit"
          >
            {{ _t('common.edit') }}
          </div>

          <div
            v-if="canShare"
            class="cursor-pointer"
            @click="onShareEdit"
          >
            {{
              props.pkg.owned?.shareStatus === ShareStatus.COMMUNITY
                ? _t('package.share_setting')
                : _t('package.share_on')
            }}
          </div>

          <div
            class="cursor-pointer text-red-500"
            @click="onDelete"
          >
            {{ _t('package.delete') }}
          </div>
        </template>

        <template v-else>
          <div
            class="cursor-pointer text-red-500"
            @click="onRemoveShelf"
          >
            {{ _t('package.remove') }}
          </div>
        </template>
      </div>
    </OverlayPanel>
  </div>
</template>

<script lang="ts" setup>
import { ShareStatus } from '@/api/package-source'
import { computed, ref, onMounted, nextTick, onUnmounted } from 'vue'
import DebugLabel from '../DebugLabel.vue'

import type { PackageBasic } from '@/api/package-source'
import { useCommonStore } from '@/stores/common-store'
import PackageCoverCard from '@/components/business/package/PackageCoverCard.vue'

const props = defineProps<{
  pkg: PackageBasic
}>()

const emit = defineEmits<{
  edit: []
  shareSetting: []
  delete: []
  removeShelf: []
}>()

const menuOp = ref()
const packageCoverRef = ref<InstanceType<typeof PackageCoverCard>>()
const actualWidth = ref<number>()

const store = useCommonStore()

const isOwner = computed(() => props.pkg.owned != null)

// 可以分享
const canShare = computed(() => isOwner.value && !store.isAndroidReviewing)

// 获取组件实际渲染宽度
const updateActualWidth = () => {
  if (packageCoverRef.value?.$el) {
    const rect = packageCoverRef.value.$el.getBoundingClientRect()
    actualWidth.value = rect.width
  }
}

onMounted(async () => {
  await nextTick()
  updateActualWidth()

  // 监听窗口大小变化，更新宽度
  window.addEventListener('resize', updateActualWidth)
})

onUnmounted(() => {
  // 清理事件监听器
  window.removeEventListener('resize', updateActualWidth)
})

const toggleMenu = (event: MouseEvent) => {
  menuOp.value.toggle(event)
}

function onEdit() {
  menuOp.value.hide()

  emit('edit')
}

function onShareEdit() {
  menuOp.value.hide()

  emit('shareSetting')
}

async function onDelete() {
  menuOp.value.hide()

  emit('delete')
}

async function onRemoveShelf() {
  menuOp.value.hide()

  emit('removeShelf')
}
</script>

<style scoped>
.pkg-card {
  box-shadow: 0px 0px 2px var(--ld-pkg-border);
}

.pkg-part-1 {
  filter: drop-shadow(0px -1px 1px var(--slate-300));
}
</style>
